import React, { useContext, useEffect, useMemo } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined, DoubleRightOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { ShareDataUnityPowerMapContext, IndexTimeActive } from '../../../..';
import { getDefaultTime } from '@/pages/WgqAllMaps/utils';
import {
  apiV2ElectricMapDataElectricityOverviewPost,
  V2ElectricMapDataElectricityOverviewPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { Spin, Progress } from 'antd';
import { keepOneAndWanOrLine, keepTwoAndWanOrLine, keepOneAndWan } from '@/utils/utils';
import { isNil } from 'lodash-es';
import { calcPercent, calcRatio } from '@/pages/WgqAllMaps/utils';
import { Button, Dropdown } from '@maxtropy/components';
import dayjs from 'dayjs';
import CustomTooltip from '@/pages/WgqAllMaps/components/CustomTooltip';
import SwitchDate from '../SwitchDate';

const ElectricityCostRight = () => {
  const {
    timeResolution: activeKey,
    dateValue: value,
    configDetail,
    setActiveKey,
    setValue,
  } = useContext(ShareDataUnityPowerMapContext);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [originData, setOriginData] = React.useState<V2ElectricMapDataElectricityOverviewPostResponse>();
  const [energyStr, setEnergyStr] = React.useState<string>('');

  useEffect(() => {
    if (!value || !activeKey) return;
    setLoading(true);
    apiV2ElectricMapDataElectricityOverviewPost({
      timeResolution: activeKey === IndexTimeActive.THIS_MONTH ? 'M' : 'Y',
      start: getDefaultTime(activeKey, value).startTime,
      end: getDefaultTime(activeKey, value).endTime,
    })
      .then(res => {
        setOriginData(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [activeKey, value]);

  useEffect(() => {
    import('@maxtropy/device-customer-apis-v2').then(({ apiV2ElectricMapConfigOverviewHighConsumptionDetailPost }) => {
      apiV2ElectricMapConfigOverviewHighConsumptionDetailPost({}).then(res => {
        let str = '';
        if (!isNil(res?.energyUnitGroupId)) {
          str += `energyGroupId=${res?.energyUnitGroupId}`;
        }
        if (!isNil(res?.energyUnitId)) {
          str += `&unitId=${res?.energyUnitId}`;
        }
        setEnergyStr(str);
      });
    });
  }, []);

  const routeStr = useMemo(() => {
    if (activeKey === IndexTimeActive.THIS_YEAR) {
      return '&currentYear=true';
    } else {
      const startDate = dayjs().subtract(1, 'day').startOf('month').format('YYYYMMDD');
      const endDate = dayjs().subtract(1, 'day').endOf('day').format('YYYYMMDD');
      return `&currentYear=false&startDate=${startDate}&endDate=${endDate}`;
    }
  }, [activeKey]);

  const isAllNull = useMemo(() => {
    return isNil(originData?.peakFee) && isNil(originData?.summitFee) && isNil(originData?.plainFee);
  }, [originData]);

  const calValley = () => {
    const peakRatio = calcPercent(originData?.peakFee, originData?.totalFee);
    const summitRatio = calcPercent(originData?.summitFee, originData?.totalFee);
    const plainRatio = calcPercent(originData?.plainFee, originData?.totalFee);
    return isAllNull ? '--' : `${keepOneAndWanOrLine(100 - peakRatio - summitRatio - plainRatio)}%`;
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.container}>
        {/* 日期选择器 */}
        <div className={styles.dateRow}>
          <Dropdown
            type="link"
            getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
            trigger={['click']}
            dropdownRender={() => (
              <div className={styles.dropdown_menu_wrapper}>
                <SwitchDate activeKey={activeKey} value={value} setActiveKey={setActiveKey} setValue={setValue} />
              </div>
            )}
          >
            <span style={{ color: '#4A90E2' }}>
              {dayjs(value).format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY')}
            </span>
          </Dropdown>
        </div>

        <div className={styles.itemRow}>
          <div className={styles.overviewItem}>
            <div>需量电费(元)</div>
            <div className={styles.artistic}>{keepOneAndWanOrLine(originData?.electricityFee)}</div>
          </div>
          <div className={styles.overviewItem}>
            <div>总电量电费(元)</div>
            <div className={styles.artistic}>{keepOneAndWanOrLine(originData?.totalFee)}</div>
          </div>
          <div className={styles.overviewItem}>
            <div>每度电均价(元)</div>
            <div className={styles.artistic}>{keepTwoAndWanOrLine(originData?.avgPrice)}</div>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
              {dayjs(value).format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY')}
            </div>
          </div>
        </div>

        {/* 尖峰平谷电费 */}
        <div className={styles.elec_wrapper}>
          <div className={styles.elec_row}>
            <div className={styles.elec_card}>
              <div className={styles.elec_card_top}>
                <div className={`${styles.item_label} ${styles.peak}`}>尖电量电费(元)</div>
                <div className={styles.progress_wrapper}>
                  <Progress
                    percent={
                      originData?.summitFee && originData?.totalFee
                        ? calcPercent(originData?.summitFee, originData?.totalFee)
                        : 0
                    }
                    showInfo={false}
                    size="small"
                    strokeColor="#FF6D2C"
                  />
                </div>
              </div>
              <div className={styles.elec_card_bottom}>
                <CustomTooltip title={keepOneAndWanOrLine(originData?.summitFee)}>
                  <div className={styles.item_value}>
                    {keepOneAndWan(originData?.summitFee).value}
                    <span className={styles.unit}>{keepOneAndWan(originData?.summitFee).unit}</span>
                  </div>
                </CustomTooltip>
                <div className={styles.rate}>{calcRatio(originData?.summitFee, originData?.totalFee)}</div>
              </div>
            </div>
            <div className={styles.elec_card}>
              <div className={styles.elec_card_top}>
                <div className={`${styles.item_label} ${styles.sharp}`}>峰电量电费(元)</div>
                <div className={styles.progress_wrapper}>
                  <Progress
                    percent={
                      originData?.peakFee && originData?.totalFee
                        ? calcPercent(originData?.peakFee, originData?.totalFee)
                        : 0
                    }
                    showInfo={false}
                    size="small"
                    strokeColor="#CE90D1"
                  />
                </div>
              </div>
              <div className={styles.elec_card_bottom}>
                <CustomTooltip title={keepOneAndWanOrLine(originData?.peakFee)}>
                  <div className={styles.item_value}>
                    {keepOneAndWan(originData?.peakFee).value}
                    <span className={styles.unit}>{keepOneAndWan(originData?.peakFee).unit}</span>
                  </div>
                </CustomTooltip>
                <div className={styles.rate}>{calcRatio(originData?.peakFee, originData?.totalFee)}</div>
              </div>
            </div>
          </div>
          <div className={styles.elec_row}>
            <div className={styles.elec_card}>
              <div className={styles.elec_card_top}>
                <div className={`${styles.item_label} ${styles.flat}`}>平电量电费(元)</div>
                <div className={styles.progress_wrapper}>
                  <Progress
                    percent={
                      originData?.plainFee && originData?.totalFee
                        ? calcPercent(originData?.plainFee, originData?.totalFee)
                        : 0
                    }
                    showInfo={false}
                    size="small"
                    strokeColor="#0DB6D9"
                  />
                </div>
              </div>
              <div className={styles.elec_card_bottom}>
                <CustomTooltip title={keepOneAndWanOrLine(originData?.plainFee)}>
                  <div className={styles.item_value}>
                    {keepOneAndWan(originData?.plainFee).value}
                    <span className={styles.unit}>{keepOneAndWan(originData?.plainFee).unit}</span>
                  </div>
                </CustomTooltip>
                <div className={styles.rate}>{calcRatio(originData?.plainFee, originData?.totalFee)}</div>
              </div>
            </div>
            <div className={styles.elec_card}>
              <div className={styles.elec_card_top}>
                <div className={`${styles.item_label} ${styles.valley}`}>谷电量电费(元)</div>
                <div className={styles.progress_wrapper}>
                  <Progress
                    percent={
                      originData?.valleyFee && originData?.totalFee
                        ? calcPercent(originData?.valleyFee, originData?.totalFee)
                        : 0
                    }
                    showInfo={false}
                    size="small"
                    strokeColor="#63BC7F"
                  />
                </div>
              </div>
              <div className={styles.elec_card_bottom}>
                <CustomTooltip title={keepOneAndWanOrLine(originData?.valleyFee)}>
                  <div className={styles.item_value}>
                    {keepOneAndWan(originData?.valleyFee).value}
                    <span className={styles.unit}>{keepOneAndWan(originData?.valleyFee).unit}</span>
                  </div>
                </CustomTooltip>
                <div className={styles.rate}>{calValley()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default ElectricityCostRight;
